import {ChangeDetectorRef, Component, ElementRef, OnInit, Renderer2, ViewChild} from '@angular/core';
import {DynamicFormService} from '../../../dynamic-form/dynamic-form.service';
import {BaseFieldComponent} from '../../base/base-field/base-field.component';
import {EventDispatcherService} from '../../../events/event-dispatcher.service';
import {NgxMatDatetimepicker} from '@angular-material-components/datetime-picker';
import {TimeFieldConfig} from '../time-field.config';
import {NgxMaterialTimepickerComponent} from 'ngx-material-timepicker';
import {TranslateService} from '@ngx-translate/core';

@Component({
    selector: 'mydocma-form-time-field',
    templateUrl: './time-field.component.html',
    styleUrls: ['./time-field.component.scss']
})
export class TimeFieldComponent extends BaseFieldComponent<TimeFieldConfig> implements OnInit {
    @ViewChild('picker', {static: false})
    public picker: NgxMaterialTimepickerComponent;

    public readonly staticCssClass = 'mydocma-time-field';
    private rend: Renderer2;

    constructor(
        public readonly dynamicFormService: DynamicFormService,
        public readonly changeDetectorRef: ChangeDetectorRef,
        public readonly eventDispatcher: EventDispatcherService,
        private readonly translateService: TranslateService,
        element: ElementRef,
        renderer: Renderer2
    ) {
        super(dynamicFormService, changeDetectorRef, eventDispatcher, element, renderer);
        this.rend = renderer;
    }

    ngOnInit() {
        super.ngOnInit();
    }

    public onClick() {
        if(!this.fieldWrapper.config.useTextInput && !this.fieldWrapper.config.readonly) {
            this.picker.defaultTime = this.toDisplayTime(this.control.value) ?? (this.isEnglishLanguage() ? '12:00 AM' : '12:00');
            this.picker.open();
        }

        super.onControlClick();
    }

    public onBlur() {
        this.eventDispatcher.notifyControlBlur(this.fieldWrapper.config);
    }

    public handleToggle($event: MouseEvent) {
        if (this.fieldWrapper.config.disabled) {
            return;
        }
        this.onSuffixClick();
        // TODO: check if control.value is a valid time -> should be automatically checked when directive is implemented
        this.picker.defaultTime = this.toDisplayTime(this.control.value) ?? (this.isEnglishLanguage() ? '12:00 AM' : '12:00');
        this.picker.open();
    }

    onTimepickerValueChanged(event: string): void {
        if(!event) return;
        
        // Update the time in the timepicker popup:
        const timeParts = event.split(' ')[0].split(':');
        const timepickerDialControl = document.getElementsByClassName('timepicker-dial__control')
        const hourElement = timepickerDialControl[0];
        const minuteElement = timepickerDialControl[1];

        if (hourElement && minuteElement) {
            this.rend.setProperty(hourElement, 'value', ('0' + timeParts[0]).slice(-2));
            this.rend.setProperty(minuteElement, 'value', timeParts[1]);
        }

        this.changeDetectorRef.detectChanges();
    }

    onTimepickerConfirm(event: any) {
        const parsed = this.parseTimeTo24Hour(event);
        const valueToStore = parsed
            ? `${parsed.hours.toString().padStart(2, '0')}:${parsed.minutes.toString().padStart(2, '0')}`
            : event;
        this.control.setValue(valueToStore, { emitEvent: false });
        this.control.updateValueAndValidity();
    }

    onTimePickerClose() {
        this.onBlur();
    }

    public isEnglishLanguage(): boolean {
        return this.translateService.currentLang?.toLowerCase().startsWith('en');
    }

    private toDisplayTime(value: string): string | null {
        const parsed = this.parseTimeTo24Hour(value);
        if (!parsed) {
            return value;
        }
        if (!this.isEnglishLanguage()) {
            return `${parsed.hours.toString().padStart(2, '0')}:${parsed.minutes.toString().padStart(2, '0')}`;
        }

        const isPm = parsed.hours >= 12;
        const twelveHour = parsed.hours % 12 || 12;
        return `${twelveHour.toString().padStart(2, '0')}:${parsed.minutes.toString().padStart(2, '0')} ${isPm ? 'PM' : 'AM'}`;
    }

    private parseTimeTo24Hour(value: string): {hours: number; minutes: number} | null {
        const input = value?.trim();
        if (!input) {
            return null;
        }

        const meridianMatch = input.match(/^(\d{1,2}):(\d{1,2})\s*([aApP][mM])$/);
        if (meridianMatch) {
            const meridianHours = Math.min(12, Number(meridianMatch[1]));
            const meridianMinutes = Math.min(59, Number(meridianMatch[2]));
            const meridian = meridianMatch[3].toUpperCase();
            let hours24 = meridianHours % 12;
            if (meridian === 'PM') {
                hours24 += 12;
            }
            return {hours: hours24, minutes: meridianMinutes};
        }

        const timeMatch = input.match(/^(\d{1,2}):(\d{1,2})$/);
        if (!timeMatch) {
            return null;
        }

        return {
            hours: Math.min(23, Number(timeMatch[1])),
            minutes: Math.min(59, Number(timeMatch[2]))
        };
    }
}
